import {
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space,
} from 'antd'
import { Dayjs } from 'dayjs'

type PatientFormProps = {
  form: FormInstance<any>
  disabledState: boolean
  onFinish?: (values?: any) => void
  onFinishFailed?: (values?: any) => void
  size?: 'middle' | 'small' | 'large'
  updateType?: boolean
}

function PatientForm({
  form,
  disabledState = false,
  onFinish,
  size,
  onFinishFailed,
  updateType = false,
}: PatientFormProps) {
  const validateAge = async (_: any, value: Dayjs | null) => {
    if (value) {
      return Promise.resolve()
    } else {
      return Promise.reject('Please choose the Date of Birth')
    }
  }

  const countryCodeOptions = [{ value: '+91' }, { value: '+61' }]
  return (
    <Form
      size={size}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      requiredMark={!disabledState}
      disabled={disabledState}
      layout='vertical'
      form={form}
      className='disabledViewForm'
    >
      <Row gutter={16}>
        <Col span={4}>
          <Form.Item
            label='Title'
            name='title'
            rules={[
              { required: true, message: 'Please enter title' },
              { pattern: /^[A-Za-z.]*$/, message: 'Enter a Valid Title' },
            ]}
          >
            <Select
              options={[
                { value: 'Mr.' },
                { value: 'Mrs.' },
                { value: 'Miss' },
                { value: 'Ms.' },
                { value: 'Dr.' },
                { value: 'Prof.' },
                { value: 'Assoc.Prof.' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item
            label='First Name'
            name='firstName'
            rules={[
              { required: true, message: 'Please enter first name' },
              {
                pattern: /^[a-zA-Z0-9.'\- ]+$/,
                message: 'Enter a valid first name',
              },
            ]}
          >
            <Input name='firstName' />
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            label='Last Name'
            name='lastName'
            rules={[
              { required: true, message: 'Please enter last name' },
              {
                pattern: /^[a-zA-Z0-9.'\- ]+$/,
                message: 'Enter a valid last name',
              },
            ]}
          >
            <Input name='lastName' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={4}>
          <Form.Item
            name='sex'
            label='Gender'
            rules={[{ required: true, message: 'Please enter gender' }]}
          >
            <Select
              options={[
                { value: 'M', label: 'Male' },
                { value: 'F', label: 'Female' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item
            name='patientId'
            label='Patient ID'
            rules={[
              { required: true, message: 'Please enter Patient ID' },
              {
                max: 15,
                message: 'Patient ID should not exceed 15 characters',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            name='dob'
            label='Date of Birth'
            rules={[{ required: true, validator: validateAge }]}
          >
            <DatePicker style={{ width: '100%' }} format={'DD-MMM-YY'} 
            value={form.getFieldValue("dob") || undefined} // Ensure no default value
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={13}>
          <Form.Item
            name='emergencyContact'
            label='Emergency Contact Name'
            rules={[
              { required: true, message: 'Please enter contact person name' },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            required={true}
            label='Emergency Phone'
            rules={[
              {
                required: true,
                message: 'Please enter emergency phone number',
              },
              {
                pattern: updateType ? /^\+(\d{1,4})\d{7,10}$/ : /^\d{7,10}$/,
                message: 'Please enter a valid phone number',
              },
            ]}
            extra='Use +61 format. Example: +61412345678'
          >
            <Space.Compact style={{ width: '100%' }}>
              {!updateType && (
                <Form.Item name={'countryCode'} noStyle>
                  <Select
                    style={{ width: '80px' }}
                    options={countryCodeOptions}
                  />
                </Form.Item>
              )}
              <Form.Item
                name='emergencyPhone'
                noStyle
                rules={[
                  {
                    required: true,
                    message: 'Please enter emergency phone number',
                  },
                  {
                    pattern: updateType
                      ? /^\+(\d{1,4})\d{7,10}$/
                      : /^\d{7,10}$/,
                    message: 'Please enter a valid phone number',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={13}>
          <Form.Item
            label='Emergency Phone 2'
            extra='Use +61 format. Example: +61412345678'
          >
            <Space.Compact style={{ width: '100%' }}>
              {!updateType && (
                <Form.Item name={'countryCode2'} noStyle>
                  <Select
                    style={{ width: '80px' }}
                    options={countryCodeOptions}
                  />
                </Form.Item>
              )}
              <Form.Item
                name='emergencyPhone2'
                noStyle
                rules={[
                  {
                    pattern: updateType
                      ? /^\+(\d{1,4})\d{7,10}$/
                      : /^\d{7,10}$/,
                    message: 'Please enter a valid phone number',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            name='emailId'
            label='Contact Person Email'
            rules={[{ message: 'Please enter valid email', type: 'email' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default PatientForm
